import { api } from '../apiClient';

/**
 * OIDC 로그인 연동 — 콘솔 화면이 부르는 쪽 (#142)
 *
 * 서비스는 사용자를 **콘솔 화면** `/oidc/authorize` 로 보낸다. 그 화면이 이 함수로
 * 코드를 받아 서비스 콜백으로 이동시킨다.
 *
 * 백엔드 `/api/oidc/authorize` 를 서비스가 직접 가리킬 수 없다 — `Authorization` 헤더를
 * 요구하는데 브라우저는 리다이렉트에 그 헤더를 싣지 못한다. 그래서 사용자가 403 을 봤다.
 */

/** 서비스가 보낸 OIDC 요청 값. 우리가 만들지 않고 그대로 넘긴다 */
export interface AuthorizeParams {
  client_id: string;
  redirect_uri: string;
  response_type?: string;
  scope?: string;
  state?: string;
  nonce?: string;
  code_challenge?: string;
  code_challenge_method?: string;
  /** RFC 8707 — MCP 클라이언트가 보낸 자원(제품 MCP 서버) 주소 (#183) */
  resource?: string;
  /** 동적(MCP) 클라이언트의 동의 결과 (#216). 사용자가 누른 것만 싣는다 */
  consent?: 'approve' | 'deny';
}

/** 동적(MCP) 클라이언트가 코드를 받기 전에 사용자에게 보여 줄 것 (#216) */
export interface ConsentPrompt {
  consent_required: true;
  client: { client_id: string; name: string; verified: boolean; redirect_host: string };
  resource: { uri: string; product: string };
  scope: string;
  /** 허용하면 앱에 나가는 사용자 정보(claim 이름). 여기 없는 것은 나가지 않는다 */
  shared_claims: string[];
}

export type AuthorizeResult = { redirect_to: string } | ConsentPrompt;

export const oidcAPI = {
  /**
   * 코드를 만들고 **돌아갈 주소**를 받는다.
   *
   * 클라이언트나 콜백 주소가 틀리면 400 이고, 그 주소는 응답에 담기지 않는다 —
   * 틀린 주소로 코드를 흘리지 않기 위한 성질이다.
   *
   * 동적(MCP) 클라이언트는 코드 대신 동의 요청(`consent_required`)이 먼저 온다 (#216).
   */
  authorize: (params: AuthorizeParams): Promise<AuthorizeResult> =>
    api.get('/oidc/authorize', { params }).then((response) => response.data),
};
